// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ReplacementLink from "@pace/App/Components/Common/ReplacementLink";
import ProductActions, { ProductActionsProps, ProductActionsStyle } from "@pace/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@pace/App/Components/Product/ProductGridTemplate";
import ProductTooltip, { ProductTooltipStyles } from "@pace/App/Components/Product/ProductTooltip";
import ProductDetailsReplacementAnchor from "@pace/App/Components/ProductDetails/ProductDetailsReplacementAnchor";
import xUseAddToCart from "@pace/App/Hooks/xUseAddToCart";
import Tooltip, { TooltipPresentationProps } from "@pace/MobiusOverrides/Tooltip";
import { ProductAvailabilityStyles } from "@pace/Overrides/Components/ProductAvailability";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@pace/Overrides/Widgets/ProductList/ProductListProductImage";
import IconHelp from "@pace/XDL/Icons/IconHelp";
import { mergeWithCss, safeJsonParse } from "@pace/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showImageBadges?: boolean;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    showInfo?: boolean;
    maximumNumberOfImageBadges?: number;
    hideAvailabilityLink?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
    containerClassName?: string;
    isPdp?: boolean;
    onChangeCompare?: (checked: boolean) => void;
    goToPageLink?: string;
    goToPageLabel?: string;
    showReplacment?: boolean;
    showReplacmentAnchor?: boolean;
}
const mapStateToProps = (state: ApplicationState) => ({
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    isAuthenticated: state.context.session.isAuthenticated,
    // Xng-Change
    location: getLocation(state),
});

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

type Props = ProductPriceAndActionsProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    priceAvailabilityLink?: LinkPresentationProps;
    infoTooltip?: ProductTooltipStyles;
    imageBadgesStyles?: ProductImageBadgesStyles;
    priceBadgesContainer?: InjectableCss;
    loginActionButton?: ButtonProps;
    goToPage?: LinkPresentationProps;
    addPackTooltip?: TooltipPresentationProps;
    addPackButton?: ButtonPresentationProps;
    // XNG-Changes
    replacementWrapper?: InjectableCss;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    showImageBadges,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    hideAvailabilityLink = false,
    showInfo = true,
    maximumNumberOfImageBadges = 5,
    productContext,
    canSeePrices,
    signInUrl,
    onAddToCartClick,
    showDisabled,
    isAuthenticated,
    containerClassName,
    isPdp,
    onChangeCompare,
    goToPageLabel,
    goToPageLink,
    location,
    addToCart,
    // XNG-Changes
    showReplacment = false,
    showReplacmentAnchor = false,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));
    const history = useHistory();

    const priceAvailabilityLinkClickHandler = () => {
        const returnUrl = location.pathname + location.search;
        if (returnUrl === "/") {
            history.push(`${signInUrl || "/MyAccount/SignIn"}`);
        } else {
            history.push(`${signInUrl || "/MyAccount/SignIn"}?returnUrl=${encodeURIComponent(returnUrl)}`);
        }
    };

    const { imageBadges } = useWidgetBadges({ productContext, showImageBadges });
    const externalDoc = safeJsonParse(productContext.product?.properties?.misExtDesc || "[]");
    const externalDocNote = Array.isArray(externalDoc) ? externalDoc.join("\n") : "";
    const packQty = parseInt(productContext.product.packDescription, 10);
    const qty = productContext.productInfo.qtyOrdered;

    const { addToCartClickHandler, canAddToCart, isAddToCartButtonDisabled } = xUseAddToCart({
        productContext,
        addToCartChainHandler: addToCart,
    });

    const addPackToCart = () => {
        if (canAddToCart && packQty > 1 && !isAddToCartButtonDisabled) {
            addToCartClickHandler(packQty * qty);
        }
    };

    if (!isAuthenticated) {
        return (
            <ProductGridContainer
                data-test-selector="product-actions-grid-template"
                extendedStyles={styles.productGridContainer}
                className={containerClassName}
            >
                {showImageBadges && !!imageBadges && (
                    <ProductImageBadges
                        badgeList={imageBadges}
                        maxBadges={maximumNumberOfImageBadges}
                        extendedStyles={styles.imageBadgesStyles}
                    />
                )}
                {(showCompare || (showInfo && !!externalDocNote)) && (
                    <ProductGridContainer.AddToList>
                        <ProductListProductImage
                            showImage={false}
                            showCompare={showCompare}
                            extendedStyles={styles.productListProductImageStyles}
                        />
                        {showInfo && !!externalDocNote && (
                            <ProductTooltip tooltipText={externalDocNote} extendedStyles={styles.infoTooltip} />
                        )}
                    </ProductGridContainer.AddToList>
                )}

                {showAddToCartActions && (
                    <ProductGridContainer.ActionFooterContainer>
                        <Button
                            data-test-selector="ProductAction-login-for-pricing"
                            aria-label="Login for Pricing & Availability"
                            onClick={priceAvailabilityLinkClickHandler}
                            {...styles.loginActionButton}
                        >
                            {translate("Login for Pricing & Availability")}
                        </Button>
                    </ProductGridContainer.ActionFooterContainer>
                )}
                {/* XNG-Changes */}
                <StyledWrapper {...styles.replacementWrapper} className="replacement-link-wrapper">
                    {showReplacment && (
                        <ReplacementLink
                            replacementProductJson={productContext.product.properties?.replacementProductJson}
                        />
                    )}
                </StyledWrapper>
                {/* XNG-Changes: PAC-1377 */}
                {showReplacmentAnchor && <ProductDetailsReplacementAnchor />}
            </ProductGridContainer>
        );
    }

    return (
        <ProductGridContainer
            data-test-selector="product-actions-grid-template"
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
        >
            <ProductGridContainer.PriceAndActions>
                <ProductGridContainer.ActionItem>
                    <StyledWrapper {...styles.priceBadgesContainer}>
                        {showPrice && (
                            <ProductGridContainer.Price>
                                <ProductPrice
                                    product={productContext}
                                    showInvalidPriceMessage={true}
                                    showLabel={false}
                                    showPack={false}
                                    extendedStyles={styles.productPriceStyles}
                                />
                            </ProductGridContainer.Price>
                        )}
                        {showImageBadges && !!imageBadges && (
                            <ProductImageBadges
                                badgeList={imageBadges}
                                maxBadges={maximumNumberOfImageBadges}
                                extendedStyles={styles.imageBadgesStyles}
                            />
                        )}
                    </StyledWrapper>
                </ProductGridContainer.ActionItem>

                {canSeePrices && showAvailability && (
                    <ProductGridContainer.Availability>
                        <ProductContextAvailability
                            extendedStyles={styles.productAvailabilityStyles}
                            isProductDetailsPage={isPdp}
                            {...{ hideLink: hideAvailabilityLink }}
                        />
                    </ProductGridContainer.Availability>
                )}
                {canSeePrices && (showAddToList || showCompare || (!!goToPageLink && !!goToPageLabel)) && (
                    <ProductGridContainer.AddToList>
                        <ProductListProductImage
                            showImage={false}
                            showCompare={showCompare}
                            extendedStyles={styles.productListProductImageStyles}
                            onChangeCompare={onChangeCompare}
                        />
                        {showAddToList && <ProductAddToListLink extendedStyles={styles.productAddToListStyles} />}
                        {!!goToPageLink && !!goToPageLabel && (
                            <Link {...styles.goToPage} href={goToPageLink} target="_blank" aria-label="go-to-page">
                                {goToPageLabel}
                            </Link>
                        )}
                    </ProductGridContainer.AddToList>
                )}
                {canSeePrices && showAddToCartActions && (
                    <ProductGridContainer.AddToCartActions>
                        <ProductActions
                            {...productActionsProps}
                            onAddToCartClick={onAddToCartClick}
                            showDisabled={showDisabled}
                            iconOnly={showAddToCartIcon}
                            extendedStyles={styles.productActionsStyles}
                        />
                    </ProductGridContainer.AddToCartActions>
                )}
                {isPdp && packQty > 1 && (
                    <Button onClick={addPackToCart} {...styles.addPackButton}>
                        {translate("Add pack to Cart ({0} units)", `${packQty * qty}`)}
                        {/* Object.keys length is to check if the site message has value or not as siteMessage returns an object */}
                        {Object.keys(siteMessage("Custom_Add_Pack_Description")).length > 0 && (
                            <Tooltip
                                triggerComponent={<Icon src={IconHelp} />}
                                text={siteMessage("Custom_Add_Pack_Description", `${packQty}`)}
                                {...styles.addPackTooltip}
                            />
                        )}
                    </Button>
                )}
                {/* XNG-Changes */}
                <StyledWrapper {...styles.replacementWrapper} className="replacement-link-wrapper">
                    {showReplacment && (
                        <ReplacementLink
                            replacementProductJson={productContext.product.properties?.replacementProductJson}
                        />
                    )}
                </StyledWrapper>
                {/* XNG-Changes: PAC-1377 */}
                {showReplacmentAnchor && <ProductDetailsReplacementAnchor />}
            </ProductGridContainer.PriceAndActions>
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductPriceAndActions));
